import i18n from 'live/services/i18n'
import { EntityTypes } from '../temporary-data-table/components/MoveEntityButton'
import { TemporaryDataType } from '../temporary-data-table/useTemporaryDataState'
import { entitiesHierarchy } from './entitiesHierarchy'

export type EntityPathMapping = {
    orderNumber: number
    entityKey: keyof TemporaryDataType
    entity: EntityTypes
}
export const pathHierarchyToEntity = ({
    data,
    entity,
    startFromEntity = 'wells',
    omitEntityKeys = [],
    unit = {}
}: {
    data: TemporaryDataType
    entity: EntityTypes
    startFromEntity?: keyof TemporaryDataType
    omitEntityKeys?: (keyof TemporaryDataType)[]
    unit?: {
        [key in keyof TemporaryDataType]?: string
    }
}): {
    entityPath: string
    entityNamePath: string
} => {
    if (!startFromEntity) startFromEntity = 'wells'

    if (!omitEntityKeys || omitEntityKeys.some(o => !o)) omitEntityKeys = []

    const entitiesChecked: (keyof TemporaryDataType)[] = []
    const entityPathMapping: EntityPathMapping[] = []

    entitiesHierarchy(data, entity, entityPathMapping, entitiesChecked, false)

    // find the index of the specified startFromEntity in the entityPathMapping array. If startFromEntity is not found, entityIndex will be -1 and the entire path will be returned (minus any omitted entities).
    const entityIndex = entityPathMapping?.findIndex(e => e?.entityKey === startFromEntity)

    // mapping of entity keys to their identifying parameters in the data, used to build the path string. The first parameter found with a value will be used as the identifier for that entity in the path.
    const paramsByEntity: { [key in keyof TemporaryDataType]: string[] } = {
        wells: ['wellName', 'wells'],
        sections: ['oD', 'name', 'type', 'riser', 'sections'],
        rigTables: ['type', 'status', 'rigTables'],
        runs: ['number', 'type', 'runs'],
        interventions: ['name', 'tatic', 'responsible', 'interventions'],
        tubulars: ['tubularType', 'oD', 'connectionType', 'materialType', 'tubulars'],
        pipeMovements: ['restrictedPipeMovementType', 'pipeMovements'],
        connections: ['restrictionType', 'connections'],
        rops: ['restrictedRopType', 'rops'],
        cements: ['startMD', 'endMD']
    }

    const entitySlicedAndFiltered = entityPathMapping
        ?.slice(0, entityIndex + 1)
        ?.filter(e => !omitEntityKeys?.includes(e?.entityKey))
        ?.reverse()

    const entityPath = entitySlicedAndFiltered?.map(e => pathWithUnit(e, paramsByEntity, unit))?.join(' - ')

    const entityNamePath = entitySlicedAndFiltered?.map(e => keySingular(e?.entityKey)).join(' - ') // using the singular form of the entity key for translation, e.g., 'wells' becomes 'well', 'sections' becomes 'section', etc.

    return { entityPath, entityNamePath }
}

// helper function to construct the path string for an entity, including the unit if available. It looks up the value for the entity using the paramsByEntity mapping and appends the unit in parentheses if a unit is defined for that entity key.
export const pathWithUnit = (
    entityMap: EntityPathMapping,
    paramsByEntity: { [key in keyof TemporaryDataType]: string[] },
    unit: Record<keyof TemporaryDataType, string>
): string => {
    const entityMapKey = entityMap?.entityKey
    const entityMapCurrentEntity = entityMap?.entity
    const paramsByEntities = paramsByEntity?.[entityMapKey]
    const value = paramsByEntities?.find(param => entityMapCurrentEntity?.[param])
    const resutl = entityMapCurrentEntity?.[value]

    const unitByEntity =
        unit?.[entityMapKey] !== undefined
            ? unit[entityMapKey] && unit?.[entityMapKey] !== null
                ? unit[entityMapKey]
                : ''
            : undefined

    return `${resutl}${unitByEntity ? ` (${unitByEntity})` : ''}`
}

// helper function to convert plural entity keys to singular form for translation purposes. This is a simple implementation and may not cover all edge cases of English pluralization, but it works for the specific entity keys used in this context.
export const keySingular = (key: keyof TemporaryDataType): string => `${i18n(`importer.${key.slice(0, -1)}`)}`

export function validateEntityParams(
    entityMap: EntityPathMapping,
    paramsByEntity: { [key in keyof TemporaryDataType]: string[] }
): string {
    const entityKey = entityMap.entityKey
    const entity = entityMap.entity

    if (!entityKey || !entity) {
        return ''
    }

    const params = paramsByEntity[entityKey]
    if (!params) {
        return ''
    }

    const paramWithValue = params.find(param => entity[param])

    return entity[paramWithValue] || ''
}
